import type { SidekickArtifactDownloadedEvent } from '../components/AISidekickPanel/SidekickPanelContext';
import {
  SIDEKICK_FEATURE_EVENT,
  type SidekickFeatureEvent,
} from '../featureEvents';
import {
  SIDEKICK_LIFECYCLE_EVENT,
  type SidekickLifecycleEvent,
} from '../lifecycle';

/**
 * Heap names of every Sidekick event, shared by each app that hosts it so
 * one Heap report covers all of them.
 */
export const SIDEKICK_HEAP_EVENTS = {
  CHART_DOWNLOADED: 'CS Sidekick Chart Downloaded',
  TABLE_DOWNLOADED: 'CS Sidekick Table Downloaded',
  PANEL_OPEN: 'CS Sidekick Panel Open',
  SESSION_START: 'CS Sidekick Session Start',
  QUERY_SUBMITTED: 'CS Sidekick Query Submitted',
  RESPONSE_RECEIVED: 'CS Sidekick Response Received',
  FEEDBACK_SUBMITTED: 'CS Sidekick Feedback Submitted',
  SESSION_END: 'CS Sidekick Session End',
  MESSAGE_COPIED: 'CS Sidekick Message Copied',
  RESPONSE_SHARED: 'CS Sidekick Response Shared',
  RESPONSE_STOPPED: 'CS Sidekick Response Stopped',
  QUERY_EDITED: 'CS Sidekick Query Edited',
  BRANCH_SWITCHED: 'CS Sidekick Branch Switched',
  CONVERSATION_RESET: 'CS Sidekick Conversation Reset',
  SUGGESTION_SELECTED: 'CS Sidekick Suggestion Selected',
  UPSELL_VIEWED: 'CS Sidekick Upsell Viewed',
  EXPLORE_PLANS_CLICKED: 'CS Sidekick Explore Plans Clicked',
  CONSENT_ACCEPTED: 'CS Sidekick Consent Accepted',
} as const;

/** Heap names of the artifact downloads, by artifact type. */
export const SIDEKICK_ARTIFACT_HEAP_EVENTS = {
  chart: SIDEKICK_HEAP_EVENTS.CHART_DOWNLOADED,
  table: SIDEKICK_HEAP_EVENTS.TABLE_DOWNLOADED,
} as const satisfies Record<
  SidekickArtifactDownloadedEvent['artifactType'],
  string
>;

/** Heap names of the lifecycle events. */
export const SIDEKICK_LIFECYCLE_HEAP_EVENTS = {
  [SIDEKICK_LIFECYCLE_EVENT.PANEL_OPENED]: SIDEKICK_HEAP_EVENTS.PANEL_OPEN,
  [SIDEKICK_LIFECYCLE_EVENT.SESSION_STARTED]:
    SIDEKICK_HEAP_EVENTS.SESSION_START,
  [SIDEKICK_LIFECYCLE_EVENT.QUERY_SUBMITTED]:
    SIDEKICK_HEAP_EVENTS.QUERY_SUBMITTED,
  [SIDEKICK_LIFECYCLE_EVENT.RESPONSE_RECEIVED]:
    SIDEKICK_HEAP_EVENTS.RESPONSE_RECEIVED,
  [SIDEKICK_LIFECYCLE_EVENT.FEEDBACK_SUBMITTED]:
    SIDEKICK_HEAP_EVENTS.FEEDBACK_SUBMITTED,
  [SIDEKICK_LIFECYCLE_EVENT.SESSION_ENDED]: SIDEKICK_HEAP_EVENTS.SESSION_END,
} as const satisfies Record<SidekickLifecycleEvent['type'], string>;

/** Heap names of the feature events. */
export const SIDEKICK_FEATURE_HEAP_EVENTS = {
  [SIDEKICK_FEATURE_EVENT.MESSAGE_COPIED]: SIDEKICK_HEAP_EVENTS.MESSAGE_COPIED,
  [SIDEKICK_FEATURE_EVENT.RESPONSE_SHARED]:
    SIDEKICK_HEAP_EVENTS.RESPONSE_SHARED,
  [SIDEKICK_FEATURE_EVENT.RESPONSE_STOPPED]:
    SIDEKICK_HEAP_EVENTS.RESPONSE_STOPPED,
  [SIDEKICK_FEATURE_EVENT.QUERY_EDITED]: SIDEKICK_HEAP_EVENTS.QUERY_EDITED,
  [SIDEKICK_FEATURE_EVENT.BRANCH_SWITCHED]:
    SIDEKICK_HEAP_EVENTS.BRANCH_SWITCHED,
  [SIDEKICK_FEATURE_EVENT.CONVERSATION_RESET]:
    SIDEKICK_HEAP_EVENTS.CONVERSATION_RESET,
  [SIDEKICK_FEATURE_EVENT.SUGGESTION_SELECTED]:
    SIDEKICK_HEAP_EVENTS.SUGGESTION_SELECTED,
  [SIDEKICK_FEATURE_EVENT.UPSELL_VIEWED]: SIDEKICK_HEAP_EVENTS.UPSELL_VIEWED,
  [SIDEKICK_FEATURE_EVENT.EXPLORE_PLANS_CLICKED]:
    SIDEKICK_HEAP_EVENTS.EXPLORE_PLANS_CLICKED,
  [SIDEKICK_FEATURE_EVENT.CONSENT_ACCEPTED]:
    SIDEKICK_HEAP_EVENTS.CONSENT_ACCEPTED,
} as const satisfies Record<SidekickFeatureEvent['type'], string>;
